// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * Health and lifecycle badge.
 *
 * `docs/05-CODING-STANDARDS.md` §26 forbids colour-only meaning, so every badge
 * carries its state as text. `ADM-014` forbids rendering missing telemetry as
 * healthy, so an unobserved value renders as "Not reported" rather than
 * defaulting to a healthy colour.
 */

import Chip from '@mui/material/Chip';
import type { ReactNode } from 'react';

import type { HealthState } from '../api-clients/views';

/** Presentation for each health state. */
const HEALTH_PRESENTATION: Record<
  HealthState,
  { label: string; color: 'success' | 'warning' | 'error' | 'default' }
> = {
  healthy: { label: 'Healthy', color: 'success' },
  degraded: { label: 'Degraded', color: 'warning' },
  unhealthy: { label: 'Unhealthy', color: 'error' },
  unknown: { label: 'Not reported', color: 'default' },
};

/** Props for {@link HealthBadge}. */
export interface HealthBadgeProps {
  /** Reported health state. */
  readonly health: HealthState;
  /**
   * Whether a measurement was actually obtained.
   *
   * When `false`, the badge reports "Not reported" regardless of `health`.
   */
  readonly observed?: boolean;
  /** What the badge describes, used to build its accessible name. */
  readonly subject: string;
}

/** Render a health state as text plus colour. */
export function HealthBadge({ health, observed = true, subject }: HealthBadgeProps): ReactNode {
  const effective: HealthState = observed ? health : 'unknown';
  const presentation = HEALTH_PRESENTATION[effective];
  return (
    <Chip
      size="small"
      variant="outlined"
      color={presentation.color}
      label={presentation.label}
      aria-label={`${subject}: ${presentation.label}`}
    />
  );
}

/** Props for {@link StateBadge}. */
export interface StateBadgeProps {
  /** Text shown and announced. */
  readonly label: string;
  /** Emphasis for the badge. */
  readonly tone?: 'neutral' | 'positive' | 'caution' | 'negative';
  /** What the badge describes, used to build its accessible name. */
  readonly subject: string;
}

const TONE_COLOR = {
  neutral: 'default',
  positive: 'success',
  caution: 'warning',
  negative: 'error',
} as const;

/** Render an arbitrary lifecycle state as text plus colour. */
export function StateBadge({ label, tone = 'neutral', subject }: StateBadgeProps): ReactNode {
  return (
    <Chip
      size="small"
      variant="outlined"
      color={TONE_COLOR[tone]}
      label={label}
      aria-label={`${subject}: ${label}`}
    />
  );
}
